import { useState } from "react";
import { useDroppable, useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Umbrella, Clock, CircleUser, Tag, StickyNote, AlertTriangle, LogOut, RotateCcw, GripVertical, MessageSquareText, Pencil, Bell, PowerOff } from "lucide-react";
import { TYPE_META, fmtTime, clientKey, shortName } from "./GuestCard";
import { TagChips } from "./TagChips";
import { TagEditor } from "./TagEditor";
import { NoteEditor } from "./NoteEditor";

export const LoungeTile = ({ lounge, occupant, reservation, activeType, noShow = false, note = "", highlight = false, orderCount = 0, onGone, onUnassign, onToggleArrived, onSaveTags, onSaveNote, onEditSpot, onGuestClick }) => {
  const inactive = lounge.active === false;
  const occupied = !!occupant;
  const { setNodeRef, isOver } = useDroppable({ id: `lounge-${lounge.id}`, data: { lounge }, disabled: inactive && !occupied });

  const meta = TYPE_META[lounge.type];
  const arrived = occupied && occupant.status === "arrived";
  const crossType = occupied && occupant.lounge_type && occupant.lounge_type !== lounge.type;
  const matchOver = isOver && activeType === lounge.type;
  const tags = reservation?.tags || [];
  const pax = reservation?.party_size;
  const num = lounge.num != null ? lounge.num : (lounge.label || "").replace(/\D+/g, "");
  const code = (lounge.type === "umbrella" ? "U" : "G") + num;

  const drag = useDraggable({
    id: `alloc-${occupant?.booking_id || lounge.id}`,
    data: { reservation: occupant, fromLounge: lounge.id },
    disabled: !occupied,
  });
  const dragStyle = drag.transform
    ? { transform: CSS.Translate.toString(drag.transform), opacity: drag.isDragging ? 0.4 : 1 }
    : undefined;
  const [expanded, setExpanded] = useState(false);

  let shell = "border-dashed border-sand-400 bg-white/50";
  if (occupied && !arrived) shell = "border-amber-400/70 bg-amber-400/[0.10]";
  if (arrived) shell = "border-sea/70 bg-sea/[0.12] seated-glow";
  if (noShow) shell = "!border-destructive bg-destructive/40 noshow-glow ring-2 ring-destructive/50";
  if (inactive && !occupied) shell = "border-slate-300 bg-slate-200/60";
  if (isOver && !occupied && !inactive) shell = matchOver
    ? "!border-solid !border-sage !bg-sage/20 ring-2 ring-sage/40"
    : "!border-solid !border-amber-500 !bg-amber-500/10 ring-2 ring-amber-500/40";

  return (
    <div
      ref={setNodeRef}
      data-testid={`lounge-tile-${lounge.id}`}
      data-status={occupied ? (noShow ? "no-show" : occupant.status) : "free"}
      className={[
        "relative flex min-h-[128px] flex-col rounded-xl border-2 p-2 transition-all duration-150",
        shell,
        highlight ? "ring-4 ring-sky-400/70" : "",
      ].join(" ")}
    >
      {orderCount > 0 && (
        <span data-testid={`order-badge-${lounge.id}`}
          className="absolute -right-1.5 -top-1.5 z-10 flex h-5 min-w-[20px] items-center justify-center gap-0.5 rounded-full bg-brand px-1 text-[10px] font-bold text-white shadow ring-2 ring-white">
          <Bell size={9} /> {orderCount}
        </span>
      )}
      {occupied ? (
        <>
          {/* Info block: drag via the grip · tap anywhere to expand notes */}
          <div ref={drag.setNodeRef} style={dragStyle} onClick={() => setExpanded((v) => !v)} className="select-none">
            {/* Line 1: grip + code + Wait/Arrived */}
            <div className="flex items-center gap-1.5">
              <span
                ref={drag.setActivatorNodeRef}
                {...drag.listeners}
                {...drag.attributes}
                data-testid={`move-handle-${lounge.id}`}
                onClick={(e) => e.stopPropagation()}
                title="Drag to move to another spot"
                className="-m-1 flex shrink-0 cursor-grab items-center rounded p-1 text-slate-400 active:cursor-grabbing"
              >
                <GripVertical size={14} />
              </span>
              <span className="shrink-0 font-display text-[13px] font-bold text-slate-500">{code}</span>
              <button
                data-testid={`arrive-${lounge.id}`}
                data-state={arrived ? "arrived" : "wait"}
                onClick={(e) => { e.stopPropagation(); onToggleArrived(lounge.id); }}
                className={[
                  "flex h-7 flex-1 items-center justify-center gap-1.5 rounded-lg text-[12px] font-semibold transition-colors",
                  arrived ? "bg-sea text-white hover:bg-sea-600" : "bg-amber-400 text-amber-950 hover:bg-amber-500",
                ].join(" ")}
                title={arrived ? "Guest arrived — tap to set back to Wait" : "Waiting — tap to mark guest Arrived"}
              >
                {arrived ? (<><CircleUser size={13} /> Arrived</>) : (<><Clock size={13} /> Wait</>)}
              </button>
            </div>

            {/* Line 2: guest name + pax */}
            <div className="mt-1 flex items-center gap-1.5">
              <span title="Tap for guest details & orders"
                onClick={(e) => { e.stopPropagation(); onGuestClick && onGuestClick({ lounge, occupant, reservation, note }); }}
                data-testid={`guest-name-${lounge.id}`}
                className="min-w-0 flex-1 truncate font-display text-[17px] font-bold leading-tight text-slate-900 hover:text-brand-700 hover:underline">{shortName(occupant.guest_name)}</span>
              {crossType && (
                <span data-testid={`mismatch-${lounge.id}`} title={`Booked ${occupant.lounge_type}, seated here`}
                  className={`inline-flex h-3 w-3 shrink-0 rounded-full ring-2 ring-white ${TYPE_META[occupant.lounge_type].dot}`} />
              )}
              {noShow && (
                <span data-testid={`noshow-${lounge.id}`} title="No-show"
                  className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-destructive px-1 py-0.5 text-[9px] font-bold uppercase text-white">
                  <AlertTriangle size={9} /> NS
                </span>
              )}
              {pax > 0 && (
                <span className="shrink-0 rounded-full bg-white/70 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{pax} pax</span>
              )}
            </div>

            {/* Line 3: time + tags + note editors */}
            <div className="mt-1 flex items-center gap-1">
              <span className="shrink-0 text-[11px] text-slate-500">{fmtTime(occupant.start_date)}–{fmtTime(occupant.end_date)}</span>
              <div className="min-w-0 flex-1"><TagChips tags={tags} /></div>
              {onEditSpot && (
                <button data-testid={`edit-spot-${lounge.id}`} onClick={(e) => { e.stopPropagation(); onEditSpot(lounge); }}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white hover:text-brand-700"
                  title="Edit spot"><Pencil size={12} /></button>
              )}
              {reservation && onSaveTags && (
                <TagEditor clientId={clientKey(reservation)} tags={tags} onSave={onSaveTags}>
                  <button data-testid={`tile-tag-${lounge.id}`} onClick={(e) => e.stopPropagation()}
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white hover:text-brand-700"
                    title="Edit tags"><Tag size={13} /></button>
                </TagEditor>
              )}
              <NoteEditor loungeId={lounge.id} note={note} onSave={onSaveNote}>
                <button data-testid={`note-btn-${lounge.id}`} onClick={(e) => e.stopPropagation()}
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-white ${note ? "text-amber-600" : "text-slate-300 hover:text-slate-500"}`}
                  title={note ? "Edit note" : "Add note"}><StickyNote size={13} /></button>
              </NoteEditor>
            </div>

            {note && (
              <p className="mt-1 line-clamp-2 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] leading-snug text-amber-800" title={note}>{note}</p>
            )}
            {expanded && (
              <div className="mt-1 border-t border-black/5 pt-1" data-testid={`tile-details-${lounge.id}`}>
                <p className="flex items-start gap-1 text-[10px] leading-snug text-slate-500">
                  <MessageSquareText size={11} className="mt-px shrink-0 text-slate-400" />
                  {reservation?.special_requests
                    ? <span className="italic">{reservation.special_requests}</span>
                    : <span className="italic text-slate-400">No guest notes</span>}
                </p>
              </div>
            )}
          </div>

          {/* Line 4: To list + Gone */}
          <div className="mt-1.5 flex items-center gap-1">
            <button
              data-testid={`unassign-${lounge.id}`}
              onClick={() => onUnassign(lounge.id)}
              className="flex h-8 flex-1 items-center justify-center gap-1 rounded-lg bg-white text-[11px] font-semibold text-slate-600 ring-1 ring-sand-300 transition-colors hover:bg-slate-800 hover:text-white hover:ring-slate-800 md:h-7"
              title="Return guest to the reservation list"
            >
              <RotateCcw size={11} /> To list
            </button>
            <button
              data-testid={`gone-${lounge.id}`}
              onClick={() => onGone(lounge.id)}
              className="flex h-8 flex-1 items-center justify-center gap-1 rounded-lg bg-white text-[11px] font-semibold text-slate-600 ring-1 ring-sand-300 transition-colors hover:bg-destructive hover:text-white hover:ring-destructive md:h-7"
              title="Guest left — free the spot"
            >
              <LogOut size={11} /> Gone
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {lounge.type === "umbrella" && <Umbrella size={13} className="text-sea" />}
              <span className="font-display text-sm font-bold text-slate-700">{code}</span>
            </div>
            <div className="flex items-center gap-1">
              {onEditSpot && (
                <button data-testid={`edit-spot-${lounge.id}`} onClick={() => onEditSpot(lounge)}
                  className="flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-white hover:text-brand-700"
                  title="Edit spot"><Pencil size={13} /></button>
              )}
              <NoteEditor loungeId={lounge.id} note={note} onSave={onSaveNote}>
                <button data-testid={`note-btn-${lounge.id}`}
                  className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-white ${note ? "text-amber-600" : "text-slate-300 hover:text-slate-500"}`}
                  title={note ? "Edit note" : "Add note"}><StickyNote size={13} /></button>
              </NoteEditor>
              <span title={`${meta.label} spot · seats ${lounge.capacity}`} data-testid={`spot-size-${lounge.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-white px-1.5 py-0.5 text-[9px] font-bold text-slate-500 ring-1 ring-sand-300">
                <span className={`h-2 w-2 rounded-full ${meta.dot}`} /> {meta.label}
              </span>
            </div>
          </div>
          {lounge.description && (
            <p className="mt-0.5 truncate text-[10px] text-slate-400" title={lounge.description}>{lounge.description}</p>
          )}
          {note && (
            <p className="mt-1 line-clamp-2 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] leading-snug text-amber-800" title={note}>{note}</p>
          )}
          <div className="mt-auto flex items-end justify-between pt-2">
            {inactive ? (
              <span data-testid={`out-of-service-${lounge.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-slate-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                <PowerOff size={10} /> Out of service
              </span>
            ) : (
              <span className="text-[11px] font-semibold uppercase tracking-wider text-sand-400">Free</span>
            )}
            <span className="text-[11px] text-sand-400">{lounge.capacity} pax</span>
          </div>
        </>
      )}
    </div>
  );
};
